<script setup>
import { computed, h, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useVueTable,
} from '@tanstack/vue-table';

import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmButton from 'dashboard/components-next/button/ConfirmButton.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import DurationInput from 'dashboard/components-next/input/DurationInput.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import ChoiceToggle from 'dashboard/components-next/input/ChoiceToggle.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import DataTable from 'dashboard/components/table/Table.vue';
import DataTablePagination from 'dashboard/components/table/Pagination.vue';
import DatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import {
  BaseTable,
  BaseTableCell,
  BaseTableRow,
} from 'dashboard/components-next/table';
import { CANONICAL_COMPONENT_GROUPS } from 'dashboard/components-next/catalog';
import frontendComponentInventory from 'dashboard/components-next/frontendComponentInventory.json';

const { t } = useI18n();

const inputValue = ref('');
const textAreaValue = ref('');
const inlineInputValue = ref('');
const durationValue = ref(30);
const durationUnit = ref('minutes');
const phoneValue = ref('');
const tagValues = ref([]);
const colorValue = ref('#1f93ff');
const editorValue = ref('');
const dateRange = ref();
const dateRangeType = ref();
const checkboxValue = ref(true);
const switchValue = ref(true);
const singleSelection = ref('primary');
const searchableSelection = ref('secondary');
const multipleSelection = ref(['primary']);
const tagSelection = ref(['secondary']);
const reorderableSelection = ref(['primary', 'secondary']);
const compactSelection = ref('primary');
const currentPage = ref(1);
const itemsPerPage = ref(15);
const inventorySearch = ref('');
const choiceValue = ref(true);
const radioSelection = ref('primary');
const activeTab = ref(0);
const dialogRef = ref(null);

const selectOptions = computed(() => [
  { label: t('BRAND_KIT.EXAMPLES.PRIMARY_OPTION'), value: 'primary' },
  { label: t('BRAND_KIT.EXAMPLES.SECONDARY_OPTION'), value: 'secondary' },
  { label: t('BRAND_KIT.EXAMPLES.TERTIARY_OPTION'), value: 'tertiary' },
]);
const navigationTabs = computed(() =>
  selectOptions.value.map((option, index) => ({
    label: option.label,
    count: (index + 1) * 4,
  }))
);
const breadcrumbItems = computed(() =>
  selectOptions.value.map(option => ({ label: option.label }))
);
const menuItems = computed(() =>
  selectOptions.value.map((option, index) => ({
    ...option,
    action: 'select',
    icon: index === 0 ? 'i-lucide-check' : 'i-lucide-circle',
  }))
);

const colorGroups = [
  {
    title: 'BRAND_KIT.COLORS.FOUNDATION',
    tokens: [
      { name: 'n-brand', className: 'bg-n-brand' },
      { name: 'n-background', className: 'bg-n-background' },
      { name: 'n-input-background', className: 'bg-n-input-background' },
      { name: 'n-surface-1', className: 'bg-n-surface-1' },
      { name: 'n-surface-2', className: 'bg-n-surface-2' },
      { name: 'n-solid-1', className: 'bg-n-solid-1' },
      { name: 'n-solid-2', className: 'bg-n-solid-2' },
      { name: 'n-solid-3', className: 'bg-n-solid-3' },
      { name: 'n-alpha-1', className: 'bg-n-alpha-1' },
      { name: 'n-alpha-2', className: 'bg-n-alpha-2' },
      { name: 'n-alpha-3', className: 'bg-n-alpha-3' },
    ],
  },
  {
    title: 'BRAND_KIT.COLORS.SEMANTIC',
    tokens: [
      { name: 'n-teal-9', className: 'bg-n-teal-9' },
      { name: 'n-amber-9', className: 'bg-n-amber-9' },
      { name: 'n-ruby-9', className: 'bg-n-ruby-9' },
      { name: 'n-slate-9', className: 'bg-n-slate-9' },
    ],
  },
];

const colorScales = [
  {
    name: 'Slate',
    classes: [
      'bg-n-slate-1',
      'bg-n-slate-2',
      'bg-n-slate-3',
      'bg-n-slate-4',
      'bg-n-slate-5',
      'bg-n-slate-6',
      'bg-n-slate-7',
      'bg-n-slate-8',
      'bg-n-slate-9',
      'bg-n-slate-10',
      'bg-n-slate-11',
      'bg-n-slate-12',
    ],
  },
  {
    name: 'Iris',
    classes: [
      'bg-n-iris-1',
      'bg-n-iris-2',
      'bg-n-iris-3',
      'bg-n-iris-4',
      'bg-n-iris-5',
      'bg-n-iris-6',
      'bg-n-iris-7',
      'bg-n-iris-8',
      'bg-n-iris-9',
      'bg-n-iris-10',
      'bg-n-iris-11',
      'bg-n-iris-12',
    ],
  },
  {
    name: 'Blue',
    classes: [
      'bg-n-blue-1',
      'bg-n-blue-2',
      'bg-n-blue-3',
      'bg-n-blue-4',
      'bg-n-blue-5',
      'bg-n-blue-6',
      'bg-n-blue-7',
      'bg-n-blue-8',
      'bg-n-blue-9',
      'bg-n-blue-10',
      'bg-n-blue-11',
      'bg-n-blue-12',
    ],
  },
  {
    name: 'Ruby',
    classes: [
      'bg-n-ruby-1',
      'bg-n-ruby-2',
      'bg-n-ruby-3',
      'bg-n-ruby-4',
      'bg-n-ruby-5',
      'bg-n-ruby-6',
      'bg-n-ruby-7',
      'bg-n-ruby-8',
      'bg-n-ruby-9',
      'bg-n-ruby-10',
      'bg-n-ruby-11',
      'bg-n-ruby-12',
    ],
  },
  {
    name: 'Amber',
    classes: [
      'bg-n-amber-1',
      'bg-n-amber-2',
      'bg-n-amber-3',
      'bg-n-amber-4',
      'bg-n-amber-5',
      'bg-n-amber-6',
      'bg-n-amber-7',
      'bg-n-amber-8',
      'bg-n-amber-9',
      'bg-n-amber-10',
      'bg-n-amber-11',
      'bg-n-amber-12',
    ],
  },
  {
    name: 'Teal',
    classes: [
      'bg-n-teal-1',
      'bg-n-teal-2',
      'bg-n-teal-3',
      'bg-n-teal-4',
      'bg-n-teal-5',
      'bg-n-teal-6',
      'bg-n-teal-7',
      'bg-n-teal-8',
      'bg-n-teal-9',
      'bg-n-teal-10',
      'bg-n-teal-11',
      'bg-n-teal-12',
    ],
  },
  {
    name: 'Gray',
    classes: [
      'bg-n-gray-1',
      'bg-n-gray-2',
      'bg-n-gray-3',
      'bg-n-gray-4',
      'bg-n-gray-5',
      'bg-n-gray-6',
      'bg-n-gray-7',
      'bg-n-gray-8',
      'bg-n-gray-9',
      'bg-n-gray-10',
      'bg-n-gray-11',
      'bg-n-gray-12',
    ],
  },
  {
    name: 'Violet',
    classes: [
      'bg-n-violet-1',
      'bg-n-violet-2',
      'bg-n-violet-3',
      'bg-n-violet-4',
      'bg-n-violet-5',
      'bg-n-violet-6',
      'bg-n-violet-7',
      'bg-n-violet-8',
      'bg-n-violet-9',
      'bg-n-violet-10',
      'bg-n-violet-11',
      'bg-n-violet-12',
    ],
  },
];

const fontFamilies = [
  { name: 'Sans', className: 'font-sans' },
  { name: 'Inter', className: 'font-inter' },
  { name: 'Inter Display', className: 'font-interDisplay' },
  { name: 'Mono', className: 'font-mono' },
];

const componentGroups = CANONICAL_COMPONENT_GROUPS;
const totalInventoryComponents = frontendComponentInventory.reduce(
  (total, group) => total + group.components.length,
  0
);
const filteredComponentInventory = computed(() => {
  const query = inventorySearch.value.trim().toLowerCase();
  if (!query) return frontendComponentInventory;

  return frontendComponentInventory
    .map(group => ({
      ...group,
      components: group.components.filter(component =>
        `${component.name} ${component.path}`.toLowerCase().includes(query)
      ),
    }))
    .filter(group => group.components.length);
});

const tableHeaders = computed(() => [
  t('BRAND_KIT_DATA.TABLE.NAME'),
  t('BRAND_KIT_DATA.TABLE.STATUS'),
  t('BRAND_KIT_DATA.TABLE.TYPE'),
]);

const serverTotalItems = 32;
const allTableItems = computed(() =>
  Array.from({ length: serverTotalItems }, (_, index) => {
    const id = index + 1;
    if (id === 1) {
      return {
        id,
        name: t('BRAND_KIT_DATA.TABLE.FIRST_ITEM'),
        status: t('BRAND_KIT_DATA.TABLE.ACTIVE'),
        type: t('BRAND_KIT_DATA.TABLE.STANDARD'),
      };
    }
    if (id === 2) {
      return {
        id,
        name: t('BRAND_KIT_DATA.TABLE.SECOND_ITEM'),
        status: t('BRAND_KIT_DATA.TABLE.PENDING'),
        type: t('BRAND_KIT_DATA.TABLE.CUSTOM'),
      };
    }
    return {
      id,
      name: `${t('BRAND_KIT_DATA.TABLE.RECORD')} ${id}`,
      status: t('BRAND_KIT_DATA.TABLE.ACTIVE'),
      type: t('BRAND_KIT_DATA.TABLE.STANDARD'),
    };
  })
);
const reportTableItems = computed(() => allTableItems.value.slice(0, 12));
const serverPageItems = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  return allTableItems.value.slice(start, start + itemsPerPage.value);
});

const reportColumnHelper = createColumnHelper();
const reportColumns = computed(() => [
  reportColumnHelper.accessor('name', {
    header: t('BRAND_KIT_DATA.TABLE.NAME'),
    cell: cell => h('span', { class: 'text-n-slate-12' }, cell.getValue()),
    size: 260,
  }),
  reportColumnHelper.accessor('status', {
    header: t('BRAND_KIT_DATA.TABLE.STATUS'),
    cell: cell => h('span', { class: 'text-n-slate-11' }, cell.getValue()),
    size: 150,
  }),
  reportColumnHelper.accessor('type', {
    header: t('BRAND_KIT_DATA.TABLE.TYPE'),
    cell: cell => h('span', { class: 'text-n-slate-11' }, cell.getValue()),
    size: 180,
  }),
]);

const reportTable = useVueTable({
  get data() {
    return reportTableItems.value;
  },
  get columns() {
    return reportColumns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  initialState: {
    pagination: { pageSize: 10 },
  },
});

const selectRadio = value => {
  radioSelection.value = value;
};

const selectTab = tab => {
  activeTab.value = navigationTabs.value.indexOf(tab);
};

const openDialog = () => dialogRef.value?.open();
</script>

<template>
  <div class="flex min-w-0 flex-col gap-10 pb-12">
    <header class="flex flex-col gap-2">
      <p class="text-xs font-medium uppercase tracking-wide text-n-brand">
        {{ $t('BRAND_KIT.EYEBROW') }}
      </p>
      <h1 class="text-2xl font-semibold text-n-slate-12">
        {{ $t('BRAND_KIT.TITLE') }}
      </h1>
      <p class="max-w-3xl text-sm text-n-slate-11">
        {{ $t('BRAND_KIT.DESCRIPTION') }}
      </p>
    </header>

    <section class="flex flex-col gap-5">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ $t('BRAND_KIT.COLORS.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ $t('BRAND_KIT.COLORS.DESCRIPTION') }}
        </p>
      </div>
      <div v-for="group in colorGroups" :key="group.title" class="space-y-3">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ $t(group.title) }}
        </h3>
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div
            v-for="token in group.tokens"
            :key="token.name"
            class="overflow-hidden rounded-xl outline outline-1 outline-n-weak"
          >
            <div :class="token.className" class="h-20" />
            <code
              class="block bg-n-surface-1 px-3 py-2 text-xs text-n-slate-11"
            >
              {{ token.name }}
            </code>
          </div>
        </div>
      </div>
      <div class="space-y-4">
        <div v-for="scale in colorScales" :key="scale.name">
          <p class="mb-2 text-xs font-medium text-n-slate-11">
            {{ scale.name }} {{ $t('BRAND_KIT.COLORS.SCALE_RANGE') }}
          </p>
          <div
            class="grid grid-cols-6 overflow-hidden rounded-lg outline outline-1 outline-n-weak sm:grid-cols-12"
          >
            <div
              v-for="(className, index) in scale.classes"
              :key="className"
              :class="className"
              class="flex h-12 items-end justify-center p-1"
              :title="className.replace('bg-', '')"
            >
              <span
                class="rounded bg-n-background/80 px-1 text-[10px] text-n-slate-12"
              >
                {{ index + 1 }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="flex flex-col gap-5">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ $t('BRAND_KIT.TYPOGRAPHY.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ $t('BRAND_KIT.TYPOGRAPHY.DESCRIPTION') }}
        </p>
      </div>
      <div class="grid gap-3 md:grid-cols-2">
        <div
          v-for="font in fontFamilies"
          :key="font.name"
          class="rounded-xl bg-n-surface-1 p-4 outline outline-1 outline-n-weak"
        >
          <p class="mb-3 text-xs font-medium text-n-slate-11">
            {{ font.name }}
          </p>
          <p :class="font.className" class="text-xl text-n-slate-12">
            {{ $t('BRAND_KIT.TYPOGRAPHY.SAMPLE') }}
          </p>
          <p :class="font.className" class="mt-2 text-sm text-n-slate-11">
            {{ $t('BRAND_KIT.TYPOGRAPHY.CHARACTER_SAMPLE') }}
          </p>
        </div>
      </div>
    </section>

    <section class="flex flex-col gap-5">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ $t('BRAND_KIT.COMPONENTS.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ $t('BRAND_KIT.COMPONENTS.DESCRIPTION') }}
        </p>
      </div>

      <div
        class="rounded-xl bg-n-surface-1 p-5 outline outline-1 outline-n-weak"
      >
        <h3 class="mb-4 text-sm font-medium text-n-slate-12">
          {{ $t('BRAND_KIT.COMPONENTS.BUTTONS') }}
        </h3>
        <div class="flex flex-wrap gap-3">
          <Button
            icon="i-lucide-check"
            :label="$t('BRAND_KIT.EXAMPLES.PRIMARY_ACTION')"
          />
          <Button
            variant="outline"
            color="slate"
            icon="i-lucide-arrow-right"
            :label="$t('BRAND_KIT.EXAMPLES.SECONDARY_ACTION')"
          />
          <Button
            variant="ghost"
            color="ruby"
            icon="i-lucide-trash-2"
            :label="$t('BRAND_KIT.EXAMPLES.DESTRUCTIVE_ACTION')"
          />
          <Button
            disabled
            color="slate"
            icon="i-lucide-ban"
            :label="$t('BRAND_KIT.EXAMPLES.DISABLED_ACTION')"
          />
          <Button
            icon="i-lucide-plus"
            size="sm"
            :label="$t('BRAND_KIT.EXAMPLES.SMALL_ACTION')"
          />
          <Button
            icon="i-lucide-settings"
            color="slate"
            :aria-label="$t('BRAND_KIT.EXAMPLES.ICON_ACTION')"
          />
          <Button
            is-loading
            icon="i-lucide-loader-circle"
            :label="$t('BRAND_KIT.EXAMPLES.LOADING_ACTION')"
          />
          <ConfirmButton
            icon="i-lucide-trash-2"
            :label="$t('BRAND_KIT.EXAMPLES.DESTRUCTIVE_ACTION')"
            :confirm-label="$t('BRAND_KIT.EXAMPLES.CONFIRM_ACTION')"
            :confirm-hint="$t('BRAND_KIT.EXAMPLES.CONFIRM_HINT')"
          />
          <ButtonGroup>
            <div class="flex">
              <Button
                class="rounded-r-none"
                icon="i-lucide-check"
                :label="$t('BRAND_KIT.EXAMPLES.PRIMARY_OPTION')"
              />
              <Button
                class="rounded-l-none"
                color="slate"
                icon="i-lucide-x"
                :label="$t('BRAND_KIT.EXAMPLES.SECONDARY_OPTION')"
              />
            </div>
          </ButtonGroup>
          <Button
            color="slate"
            icon="i-lucide-message-square-plus"
            :label="$t('BRAND_KIT.EXAMPLES.OPEN_DIALOG')"
            @click="openDialog"
          />
          <Popover>
            <Button
              color="slate"
              icon="i-lucide-chevron-down"
              :label="$t('BRAND_KIT.EXAMPLES.OPEN_MENU')"
            />
            <template #content>
              <DropdownMenu
                :menu-items="menuItems"
                class="!relative !shadow-none"
              />
            </template>
          </Popover>
        </div>
        <div class="mt-5 border-t border-n-weak pt-5">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ $t('BRAND_KIT.COMPONENTS.SETTINGS_ACTIONS') }}
          </h4>
          <div class="mt-3 flex items-center justify-between gap-4">
            <p class="text-label-small leading-4 text-n-slate-11">
              {{ $t('BRAND_KIT.EXAMPLES.SETTINGS_ACTION_DESCRIPTION') }}
            </p>
            <Button
              class="shrink-0"
              icon="i-lucide-check"
              :label="$t('BRAND_KIT.EXAMPLES.PRIMARY_ACTION')"
            />
          </div>
        </div>
        <Dialog
          ref="dialogRef"
          :title="$t('BRAND_KIT.EXAMPLES.DIALOG_TITLE')"
          :description="$t('BRAND_KIT.EXAMPLES.DIALOG_DESCRIPTION')"
          confirm-button-icon="i-lucide-check"
          cancel-button-icon="i-lucide-x"
          width="sm"
        />
      </div>

      <div class="grid gap-5 lg:grid-cols-2">
        <div class="flex flex-col gap-4">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ $t('BRAND_KIT.INVENTORY.FORMS') }}
          </h3>
          <RadioCard
            id="brand-kit-primary"
            :label="$t('BRAND_KIT.EXAMPLES.PRIMARY_OPTION')"
            :description="$t('BRAND_KIT.COMPONENTS.DESCRIPTION')"
            :is-active="radioSelection === 'primary'"
            @select="selectRadio('primary')"
          />
          <RadioCard
            id="brand-kit-secondary"
            :label="$t('BRAND_KIT.EXAMPLES.SECONDARY_OPTION')"
            :description="$t('BRAND_KIT.COMPONENTS.DESCRIPTION')"
            :is-active="radioSelection === 'secondary'"
            @select="selectRadio('secondary')"
          />
          <ChoiceToggle v-model="choiceValue" />
        </div>

        <div class="flex flex-col gap-4">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ $t('BRAND_KIT.INVENTORY.ACTIONS') }}
          </h3>
          <Breadcrumb :items="breadcrumbItems" />
          <TabBar
            :tabs="navigationTabs"
            :initial-active-tab="activeTab"
            @tab-changed="selectTab"
          />
          <Accordion :title="$t('BRAND_KIT.EXAMPLES.SECONDARY_OPTION')" is-open>
            <p class="text-sm text-n-slate-11">
              {{ $t('BRAND_KIT.COMPONENTS.DESCRIPTION') }}
            </p>
          </Accordion>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ $t('BRAND_KIT.INVENTORY.DATA') }}
        </h3>
        <div class="grid gap-3 md:grid-cols-2">
          <Banner color="blue">
            {{ $t('BRAND_KIT.COMPONENTS.DESCRIPTION') }}
          </Banner>
          <Banner color="amber">
            {{ $t('BRAND_KIT.COMPONENTS.DESCRIPTION') }}
          </Banner>
        </div>
        <div class="flex flex-wrap items-center gap-3">
          <Label
            v-for="color in ['slate', 'brand', 'blue', 'teal', 'amber', 'ruby']"
            :key="color"
            :label="$t('BRAND_KIT_DATA.TABLE.STATUS')"
            :color="color"
          />
          <Spinner :size="20" class="text-n-brand" />
          <Avatar
            :name="$t('BRAND_KIT.EXAMPLES.EXAMPLE_USER')"
            :size="32"
            rounded-full
            status="online"
          />
          <Flag country="BR" squared />
          <Icon icon="i-lucide-circle-check" class="size-5 text-n-teal-10" />
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <div>
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ $t('BRAND_KIT.FULL_INVENTORY.TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{
              $t('BRAND_KIT.FULL_INVENTORY.DESCRIPTION', {
                count: totalInventoryComponents,
              })
            }}
          </p>
        </div>
        <Input
          v-model="inventorySearch"
          :label="$t('BRAND_KIT.FULL_INVENTORY.SEARCH_LABEL')"
          :placeholder="$t('BRAND_KIT.FULL_INVENTORY.SEARCH_PLACEHOLDER')"
        />
        <div class="grid gap-3 lg:grid-cols-2">
          <div
            v-for="group in filteredComponentInventory"
            :key="group.titleKey"
            class="min-w-0 rounded-xl bg-n-surface-1 p-4 outline outline-1 outline-n-weak"
          >
            <div class="mb-3 flex items-center justify-between gap-3">
              <p class="text-sm font-medium text-n-slate-12">
                {{ $t(group.titleKey) }}
              </p>
              <span class="text-xs text-n-slate-10">
                {{ group.components.length }}
              </span>
            </div>
            <div class="max-h-72 space-y-1 overflow-y-auto">
              <div
                v-for="component in group.components"
                :key="component.path"
                class="min-w-0 rounded-md bg-n-alpha-2 px-2 py-1.5"
              >
                <code class="block text-xs text-n-slate-12">
                  {{ component.name }}
                </code>
                <code class="block truncate text-[10px] text-n-slate-10">
                  {{ component.path }}
                </code>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="grid gap-5 lg:grid-cols-2">
        <div
          class="rounded-xl bg-n-surface-1 p-5 outline outline-1 outline-n-weak"
        >
          <h3 class="mb-4 text-sm font-medium text-n-slate-12">
            {{ $t('BRAND_KIT.COMPONENTS.FORM_CONTROLS') }}
          </h3>
          <div class="flex flex-col gap-4">
            <Input
              v-model="inputValue"
              :label="$t('BRAND_KIT.EXAMPLES.INPUT_LABEL')"
              :placeholder="$t('BRAND_KIT.EXAMPLES.INPUT_PLACEHOLDER')"
            />
            <Input
              :model-value="$t('BRAND_KIT.EXAMPLES.INVALID_VALUE')"
              :label="$t('BRAND_KIT.EXAMPLES.ERROR_STATE')"
              :message="$t('BRAND_KIT.EXAMPLES.ERROR_MESSAGE')"
              message-type="error"
            />
            <Input
              :model-value="$t('BRAND_KIT.EXAMPLES.VALID_VALUE')"
              :label="$t('BRAND_KIT.EXAMPLES.SUCCESS_STATE')"
              :message="$t('BRAND_KIT.EXAMPLES.SUCCESS_MESSAGE')"
              message-type="success"
            />
            <Input
              disabled
              :model-value="$t('BRAND_KIT.EXAMPLES.DISABLED_ACTION')"
              :label="$t('BRAND_KIT.EXAMPLES.DISABLED_STATE')"
            />
            <TextArea
              v-model="textAreaValue"
              :label="$t('BRAND_KIT.EXAMPLES.TEXTAREA_LABEL')"
              :placeholder="$t('BRAND_KIT.EXAMPLES.TEXTAREA_PLACEHOLDER')"
            />
            <TextArea
              :model-value="$t('BRAND_KIT.EXAMPLES.INVALID_VALUE')"
              :label="$t('BRAND_KIT.EXAMPLES.ERROR_STATE')"
              :message="$t('BRAND_KIT.EXAMPLES.ERROR_MESSAGE')"
              message-type="error"
            />
            <div
              class="rounded-lg bg-n-alpha-2 p-3 outline outline-1 outline-n-weak"
            >
              <InlineInput
                id="brand-kit-inline-input"
                v-model="inlineInputValue"
                :label="$t('BRAND_KIT.EXAMPLES.INLINE_INPUT_LABEL')"
                :placeholder="$t('BRAND_KIT.EXAMPLES.INPUT_PLACEHOLDER')"
              />
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('BRAND_KIT.EXAMPLES.DURATION_LABEL') }}
              </span>
              <div class="flex gap-2">
                <DurationInput
                  v-model="durationValue"
                  v-model:unit="durationUnit"
                />
              </div>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('BRAND_KIT.EXAMPLES.PHONE_LABEL') }}
              </span>
              <PhoneNumberInput
                v-model="phoneValue"
                :placeholder="$t('BRAND_KIT.EXAMPLES.PHONE_PLACEHOLDER')"
              />
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('BRAND_KIT.EXAMPLES.TAG_LABEL') }}
              </span>
              <TagInput
                v-model="tagValues"
                allow-create
                :placeholder="$t('BRAND_KIT.EXAMPLES.TAG_PLACEHOLDER')"
              />
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('BRAND_KIT.EXAMPLES.COLOR_LABEL') }}
              </span>
              <ColorPicker v-model="colorValue" />
            </div>
            <Editor
              v-model="editorValue"
              editor-key="brand-kit-editor"
              :label="$t('BRAND_KIT.EXAMPLES.EDITOR_LABEL')"
              :placeholder="$t('BRAND_KIT.EXAMPLES.TEXTAREA_PLACEHOLDER')"
              :enable-canned-responses="false"
            />
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('BRAND_KIT.EXAMPLES.DATE_RANGE_LABEL') }}
              </span>
              <DatePicker
                v-model:date-range="dateRange"
                v-model:range-type="dateRangeType"
              />
            </div>
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <Checkbox v-model="checkboxValue" />
              {{ $t('BRAND_KIT.EXAMPLES.CHECKBOX_LABEL') }}
            </label>
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <Switch v-model="switchValue" />
              {{ $t('BRAND_KIT.EXAMPLES.SWITCH_LABEL') }}
            </label>
          </div>
        </div>

        <div
          class="rounded-xl bg-n-surface-1 p-5 outline outline-1 outline-n-weak"
        >
          <h3 class="mb-4 text-sm font-medium text-n-slate-12">
            {{ $t('BRAND_KIT.COMPONENTS.SELECTION') }}
          </h3>
          <div class="flex flex-col gap-4">
            <AdaptiveSelect
              v-model="singleSelection"
              :options="selectOptions"
              :placeholder="$t('BRAND_KIT.EXAMPLES.SELECT_PLACEHOLDER')"
            />
            <AdaptiveSelect
              v-model="multipleSelection"
              mode="multiple"
              :options="selectOptions"
              :placeholder="$t('BRAND_KIT.EXAMPLES.MULTISELECT_PLACEHOLDER')"
            />
            <AdaptiveSelect
              v-model="searchableSelection"
              searchable
              :options="selectOptions"
              :search-placeholder="$t('BRAND_KIT.EXAMPLES.SEARCH_PLACEHOLDER')"
              :placeholder="$t('BRAND_KIT.EXAMPLES.SELECT_PLACEHOLDER')"
            />
            <AdaptiveSelect
              v-model="tagSelection"
              mode="tags"
              :options="selectOptions"
              :placeholder="$t('BRAND_KIT.EXAMPLES.MULTISELECT_PLACEHOLDER')"
            />
            <AdaptiveSelect
              v-model="reorderableSelection"
              mode="reorderable"
              :options="selectOptions"
              :label="$t('BRAND_KIT.EXAMPLES.REORDERABLE_LABEL')"
              :add-label="$t('BRAND_KIT.EXAMPLES.ADD_OPTION')"
            />
            <AdaptiveSelect
              v-model="compactSelection"
              presentation="compact"
              :options="selectOptions"
              :placeholder="$t('BRAND_KIT.EXAMPLES.COMPACT_LABEL')"
            />
            <AdaptiveSelect
              model-value="primary"
              disabled
              :options="selectOptions"
              :placeholder="$t('BRAND_KIT.EXAMPLES.SELECT_PLACEHOLDER')"
            />
            <AdaptiveSelect
              model-value=""
              :options="selectOptions"
              :error="$t('BRAND_KIT.EXAMPLES.ERROR_MESSAGE')"
              :placeholder="$t('BRAND_KIT.EXAMPLES.ERROR_STATE')"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 class="mb-3 text-sm font-medium text-n-slate-12">
          {{ $t('BRAND_KIT.INVENTORY.TITLE') }}
        </h3>
        <div class="grid gap-3 md:grid-cols-2">
          <div
            v-for="group in componentGroups"
            :key="group.titleKey"
            class="rounded-xl bg-n-surface-1 p-4 outline outline-1 outline-n-weak"
          >
            <p class="mb-3 text-sm font-medium text-n-slate-12">
              {{ $t(group.titleKey) }}
            </p>
            <div class="flex flex-wrap gap-2">
              <code
                v-for="component in group.components"
                :key="component.path"
                class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
              >
                {{ component.name }}
              </code>
            </div>
          </div>
        </div>
      </div>

      <div
        class="rounded-xl bg-n-surface-1 p-5 outline outline-1 outline-n-weak"
      >
        <div class="border-b border-n-weak px-5 py-4">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ $t('BRAND_KIT_DATA.REPORT_TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('BRAND_KIT_DATA.REPORT_DESCRIPTION') }}
          </p>
        </div>
        <DataTable :table="reportTable" />
        <DataTablePagination
          class="mt-2"
          :table="reportTable"
          show-page-size-selector
        />
      </div>

      <div
        class="overflow-hidden rounded-xl bg-n-surface-1 outline outline-1 outline-n-weak"
      >
        <div class="border-b border-n-weak px-5 py-4">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ $t('BRAND_KIT_DATA.SERVER_TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('BRAND_KIT_DATA.SERVER_DESCRIPTION') }}
          </p>
        </div>
        <div class="px-5">
          <BaseTable :headers="tableHeaders" :items="serverPageItems">
            <template #row="{ items }">
              <BaseTableRow v-for="item in items" :key="item.id" :item="item">
                <template #default="{ item: row }">
                  <BaseTableCell>{{ row.name }}</BaseTableCell>
                  <BaseTableCell>{{ row.status }}</BaseTableCell>
                  <BaseTableCell>{{ row.type }}</BaseTableCell>
                </template>
              </BaseTableRow>
            </template>
          </BaseTable>
        </div>
        <PaginationFooter
          v-model:current-page="currentPage"
          v-model:items-per-page="itemsPerPage"
          :total-items="serverTotalItems"
          show-page-size-selector
        />
      </div>
    </section>
  </div>
</template>
